import {
  applyRoundEvent,
  attemptFromRoundState,
  defaultRandomSource,
  performanceClock,
  IDLE_ROUND_STATE,
  type Attempt,
  type RoundConfig,
  type RoundState,
} from '@ranked-waiting/game-core';
import { formatDurationMs } from './format';
import { resultBadgeMarkup, resultTier, type ResultTier } from './result-badge';
import { accuracyBarMarkup } from './accuracy-bar';
import { isTimingSoundMuted, playReleaseSound, setTimingSoundMuted, startHoldSound, stopHoldSound } from './timing-audio';
import { burstParticles, screenFlash, shake } from './timing-juice';

/** Tier → the accent colour its juice (particles, flash) is drawn in. */
const TIER_COLOUR: Record<ResultTier, string> = {
  gold: '#ffd24a',
  green: '#9de86f',
  yellow: '#f2d24b',
  orange: '#ff9d47',
  red: '#ff5c5c',
  'way-off': '#e0503f',
  'false-start': '#ff5c35',
};

export interface RoundStageOptions {
  config: RoundConfig;
  /** Force a specific target instead of generating a new one (used so both players in a hotseat round face the same target). */
  fixedTargetMs?: number;
  label: string;
  /** Force the hidden pre-round delay. Online modes pass the server's value so the randomised start is server-controlled, not client-chosen. */
  fixedPrepareDelayMs?: number;
  /**
   * Fired the instant the hidden clock actually starts. Online modes send this
   * to the server so it can timestamp the start of the attempt itself and
   * corroborate the elapsed time the client reports later. Offline modes omit it.
   */
  onArm?: () => void;
  /**
   * Fired the instant the player releases to stop.
   *
   * Providing this suppresses the built-in local reveal and hands the UI to the
   * caller. Online modes MUST use this rather than `onComplete`: the server
   * timestamps the attempt by when the stop message arrives, so deferring that
   * send until the player dismisses a reveal screen would fold their reading
   * time into the measured attempt and fail corroboration.
   */
  onStop?: (attempt: Attempt) => void;
  /** Fired when the player dismisses the built-in local reveal. Offline modes use this to advance. */
  onComplete?: (attempt: Attempt) => void;
}

/**
 * One continuous press-and-hold-then-release gesture (owner decision,
 * 2026-08-11 — the battle-royale/hype design pass) drives the whole round:
 * press down to arm the hidden clock, release the instant you believe the
 * target has elapsed. This is a *reinterpretation* of game-core's existing
 * event sequence, not a new one — `SHOW_TARGET -> BEGIN_PREPARE -> ACTIVATE
 * -> STOP|FALSE_START -> REVEAL` already models exactly this (press-down maps
 * to `BEGIN_PREPARE`, release maps to `STOP`/`FALSE_START`); only which DOM
 * gesture triggers those two calls changed, from two separate button clicks
 * to one press/release pair. `packages/game-core/src/round.ts` and the
 * server's arm/stop corroboration are untouched by this file.
 */
export function renderRoundStage(container: HTMLElement, options: RoundStageOptions): () => void {
  const { config, label } = options;
  let state: RoundState = IDLE_ROUND_STATE;
  let activateTimeoutId: number | undefined;
  let destroyed = false;
  /** Guards keydown auto-repeat from re-triggering a press while Space/Enter is already held. */
  let spaceOrEnterHeld = false;

  function render() {
    if (state.phase === 'target') {
      container.innerHTML = `
        <div class="round-stage" data-phase="target">
          <button class="round-mute" type="button" data-action="mute" aria-label="${isTimingSoundMuted() ? 'Unmute sound' : 'Mute sound'}" aria-pressed="${isTimingSoundMuted()}">${isTimingSoundMuted() ? '🔇' : '🔊'}</button>
          <p class="round-stage__label">${label}</p>
          <div class="round-stage__readout" aria-label="Target duration">
            <span>${formatDurationMs(state.targetMs ?? 0)}</span><small>SEC</small>
          </div>
          <p class="round-stage__hint">Memorise it, then press and hold. Release the instant you believe the target has elapsed.</p>
          <button class="round-hold" type="button" data-action="hold" aria-label="Press and hold to begin, release to stop">
            <span class="round-hold__label">HOLD</span>
            <small class="round-hold__hint">SPACE / TAP &amp; HOLD</small>
          </button>
        </div>
      `;
    } else if (state.phase === 'prepare' || state.phase === 'active') {
      container.innerHTML = `
        <div class="round-stage" data-phase="hidden">
          <p class="round-stage__label">${label}</p>
          <div class="round-stage__blank" aria-hidden="true"><span class="round-stage__pulse"></span></div>
          <p class="round-stage__hint">Release the instant you believe the target has elapsed.</p>
          <button class="round-hold round-hold--held" type="button" data-action="hold" aria-label="Release to stop">
            <span class="round-hold__label">HOLDING…</span>
          </button>
        </div>
      `;
    } else if (state.phase === 'reveal') {
      const attempt = attemptFromRoundState(state);
      container.innerHTML = `
        <div class="round-stage" data-phase="reveal">
          <p class="round-stage__label">${label}</p>
          <div class="round-result${attempt.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>Target</span><strong>${formatDurationMs(attempt.targetMs)}s</strong></div>
            <div><span>Your time</span><strong>${attempt.isFalseStart ? 'FALSE START' : `${formatDurationMs(attempt.elapsedMs)}s`}</strong></div>
            <div><span>Result</span>${resultBadgeMarkup(attempt)}</div>
          </div>
          ${accuracyBarMarkup(attempt)}
          <button class="button button--secondary round-stage__action" type="button" data-action="continue">Continue</button>
        </div>
      `;
    }
    attachHandlers();
  }

  function dispatchStopOrFalseStart() {
    // Capture where the button is NOW, before any re-render replaces it — the
    // release burst fires from the point the finger actually let go.
    const holdEl = container.querySelector<HTMLElement>('[data-action="hold"]');
    const origin = holdEl ? centreOf(holdEl) : null;

    const now = performanceClock.now();
    if (state.phase === 'active') {
      const result = applyRoundEvent(state, { type: 'STOP', now }, defaultRandomSource, config);
      if (!result.ok) return;
      state = result.state;
    } else if (state.phase === 'prepare') {
      if (activateTimeoutId !== undefined) {
        clearTimeout(activateTimeoutId);
        activateTimeoutId = undefined;
      }
      const result = applyRoundEvent(state, { type: 'FALSE_START', now }, defaultRandomSource, config);
      if (!result.ok) return;
      state = result.state;
    } else {
      return;
    }

    const revealed = applyRoundEvent(state, { type: 'REVEAL' }, defaultRandomSource, config);
    if (revealed.ok) state = revealed.state;

    // Release juice: kill the drone, sting + flash + particles + a shake of the
    // whole stage, all coloured by how close the attempt landed. Fires for
    // online modes too (before the onStop hand-off), so the release always
    // feels like an impact rather than a value quietly changing.
    playReleaseJuice(attemptFromRoundState(state), origin);

    // Hand off immediately if the caller owns the post-stop UI, so the stop is
    // reported without waiting on the player to dismiss anything.
    if (options.onStop) {
      options.onStop(attemptFromRoundState(state));
      return;
    }

    render();
  }

  function playReleaseJuice(attempt: Attempt, origin: { x: number; y: number } | null) {
    const tier = resultTier(attempt);
    const colour = TIER_COLOUR[tier];
    stopHoldSound();
    playReleaseSound(tier);
    const good = tier === 'gold' || tier === 'green';
    if (origin) {
      burstParticles(origin, {
        count: tier === 'gold' ? 30 : good ? 22 : 16,
        colors: [colour, '#ffffff', colour],
        spread: tier === 'gold' ? 150 : 110,
        size: tier === 'gold' ? 9 : 7,
      });
    }
    screenFlash(colour, tier === 'gold' ? 0.42 : good ? 0.3 : tier === 'false-start' ? 0.32 : 0.24);
    // A crisp shake on a miss/false-start, a softer one on a good hit — the
    // mount persists across the reveal re-render, so the class survives.
    shake(container, tier === 'false-start' || tier === 'way-off' || tier === 'red' ? 'hard' : 'soft');
  }

  function dispatchBegin() {
    if (state.phase !== 'target') return;
    const now = performanceClock.now();
    const result = applyRoundEvent(state, { type: 'BEGIN_PREPARE', now }, defaultRandomSource, config);
    if (!result.ok) return;
    state = result.state;
    render();

    // Press juice: a small spark off the button and the steady "time slows"
    // drone begins. The drone is deliberately rhythm-free (see timing-audio.ts)
    // so it can never be counted against — only the release sting carries info.
    startHoldSound();
    const pressed = container.querySelector<HTMLElement>('[data-action="hold"]');
    if (pressed) burstParticles(pressed, { count: 10, colors: ['#ff5c35', '#ffd24a', '#ffffff'], spread: 55, size: 6 });

    const delay = state.prepareDelayMs ?? 0;
    activateTimeoutId = window.setTimeout(() => {
      const activated = applyRoundEvent(state, { type: 'ACTIVATE', now: performanceClock.now() }, defaultRandomSource, config);
      if (activated.ok) {
        state = activated.state;
        options.onArm?.();
        render();
      }
    }, delay);
  }

  /** Press-start: begins the hold if we're waiting to start, otherwise a no-op (dispatchBegin already guards on phase). */
  function handlePressStart() {
    dispatchBegin();
  }

  /** Press-end (release, pointer leaving the control, or the gesture being cancelled): stops the hold if one is in progress. */
  function handlePressEnd() {
    if (state.phase === 'prepare' || state.phase === 'active') dispatchStopOrFalseStart();
  }

  function completeRound() {
    if (destroyed) return;
    const attempt = attemptFromRoundState(state);
    options.onComplete?.(attempt);
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.code !== 'Space' && event.code !== 'Enter') return;
    event.preventDefault();
    if (state.phase === 'reveal') {
      completeRound();
      return;
    }
    if (event.repeat || spaceOrEnterHeld) return; // ignore OS key-repeat while already held
    spaceOrEnterHeld = true;
    handlePressStart();
  }

  function onKeyup(event: KeyboardEvent) {
    if (event.code !== 'Space' && event.code !== 'Enter') return;
    if (!spaceOrEnterHeld) return;
    spaceOrEnterHeld = false;
    handlePressEnd();
  }

  /** Re-run after every render: the reveal "Continue" and the mute toggle are plain clicks on freshly-rendered elements. The hold gesture is NOT wired here — see `setupHoldInput`. */
  function attachHandlers() {
    const continueButton = container.querySelector<HTMLButtonElement>('[data-action="continue"]');
    continueButton?.addEventListener('click', completeRound);

    const muteButton = container.querySelector<HTMLButtonElement>('[data-action="mute"]');
    muteButton?.addEventListener('click', () => {
      setTimingSoundMuted(!isTimingSoundMuted());
      render();
    });
  }

  /** The pointer id of the finger/mouse currently driving a hold, so a stray second pointer can't end it. */
  let holdPointerId: number | null = null;
  /** Removes this round's container-level input listeners on destroy — otherwise a view that re-mounts the stage per round (Practice) would stack a fresh set every round. */
  const inputAbort = new AbortController();

  /**
   * Wires the press-and-hold gesture ONCE, on the stable `container`, not
   * per-render on the button.
   *
   * This is the crux of the long-running "press and hold instantly stops on my
   * phone" bug (owner, 2026-08). The moment a press begins, the stage
   * re-renders (target → "HOLDING…"), which REBUILDS the button — the old one
   * is removed from the DOM mid-gesture. Anything tied to that button breaks
   * the instant it's destroyed:
   *   - a `pointerleave` listener fired on a real finger's tiny drift off the
   *     button edge (the original bug), and
   *   - worse, `setPointerCapture` on the button meant that when the button was
   *     removed by the re-render the browser fired `pointercancel` on it, which
   *     the game read as an early release → a false start every single time (an
   *     earlier "fix" that made it worse, not better).
   * The container is never rebuilt (render only rewrites its innerHTML), so
   * capturing the pointer on it and listening there survives every re-render:
   * drift can't fire a boundary event, the button being replaced can't cancel
   * the capture, and the release always lands. Only a true `pointerup` or a
   * genuine system `pointercancel` (an incoming call, say) ends the hold.
   */
  function setupHoldInput() {
    // preventDefault on touchstart over the control stops the browser's own
    // scroll/long-press/selection gestures while a finger is down; { passive:
    // false } is required for it to take effect.
    const signal = inputAbort.signal;
    container.addEventListener(
      'touchstart',
      (event) => {
        if ((event.target as Element | null)?.closest('[data-action="hold"]')) event.preventDefault();
      },
      { passive: false, signal },
    );
    container.addEventListener(
      'pointerdown',
      (event) => {
        if (holdPointerId !== null) return; // already holding with another pointer
        if (!(event.target as Element | null)?.closest('[data-action="hold"]')) return;
        holdPointerId = event.pointerId;
        try {
          container.setPointerCapture(event.pointerId);
        } catch {
          /* capture unsupported; the gesture still works, it just can't survive the finger leaving the container's bounds entirely */
        }
        handlePressStart();
      },
      { signal },
    );
    const end = (event: PointerEvent) => {
      if (event.pointerId !== holdPointerId) return;
      holdPointerId = null;
      handlePressEnd();
    };
    container.addEventListener('pointerup', end, { signal });
    container.addEventListener('pointercancel', end, { signal });
  }

  window.addEventListener('keydown', onKeydown);
  window.addEventListener('keyup', onKeyup);
  setupHoldInput();

  const shown = applyRoundEvent(
    IDLE_ROUND_STATE,
    { type: 'SHOW_TARGET' },
    defaultRandomSource,
    config,
  );
  state = shown.ok
    ? {
        ...shown.state,
        targetMs: options.fixedTargetMs ?? shown.state.targetMs,
        prepareDelayMs: options.fixedPrepareDelayMs ?? shown.state.prepareDelayMs,
      }
    : IDLE_ROUND_STATE;
  render();

  return function destroy() {
    destroyed = true;
    if (activateTimeoutId !== undefined) clearTimeout(activateTimeoutId);
    // Never leave the drone running if the round is torn down mid-hold (view
    // change, reconnect, etc.).
    stopHoldSound();
    inputAbort.abort();
    window.removeEventListener('keydown', onKeydown);
    window.removeEventListener('keyup', onKeyup);
  };
}

/** Centre point of an element in viewport coordinates. */
function centreOf(el: HTMLElement): { x: number; y: number } {
  const rect = el.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}
